import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Download } from 'lucide-react';
import { AdminButton, AdminCard, AdminSectionTitle } from '../../ui';
import {
  LedgerEntry,
  TransactionLedger as TL,
  getTransactionLedger,
} from '../../../../api/payment/withdraw-request.api';

const fmt = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;
const short = (g?: string) => (g ? g.slice(0, 8) : '—');

const TYPES: Record<string, string> = {
  TopUp: 'Nạp Ca',
  PostDonate: 'Donate bài',
  ProfileDonate: 'Donate trang',
  MarketplacePurchase: 'Mua vật phẩm',
  AiTokenPurchase: 'Mua token Trợ lý',
  CabPlusPurchase: 'Mua Cab+',
  WithdrawRequest: 'Rút tiền',
  BrandLivePayout: 'Trả quảng cáo livestream',
  BrandDealPayout: 'Trả hợp tác creator',
  BrandDisputeCompensation: 'Đền bù kháng cáo',
  ChannelMembership: 'Hội viên kênh',
  MembershipCabPlusSlot: 'Lượt Cab+ hội viên',
  CreatorAdRevenue: 'Chia creator từ quảng cáo',
};

const TransactionLedger: React.FC<{ fromDate?: string; toDate?: string }> = ({ fromDate, toDate }) => {
  const [type, setType] = useState('');
  const [userId, setUserId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [take, setTake] = useState(100);
  const [data, setData] = useState<TL | null>(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    getTransactionLedger({
      type: type || undefined,
      userId: userId.trim() || undefined,
      fromDate,
      toDate,
      minAmount: minAmount ? Number(minAmount) : undefined,
      maxAmount: maxAmount ? Number(maxAmount) : undefined,
      take,
    })
      .then(setData)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [type, take, fromDate, toDate]);

  const exportCsv = () => {
    if (!data) return;
    const header = ['Thời gian', 'Loại', 'Từ (user)', 'Đến (user)', 'Chi', 'Nhận', 'Phí', 'Ghi chú'];
    const rows = data.items.map((e) => [
      new Date(e.createdAt).toLocaleString('vi-VN'),
      TYPES[e.type] || e.type,
      e.fromUserId, e.toUserId, e.spendAmount, e.receiveAmount, e.feeAmount,
      (e.message || '').replace(/"/g, "'"),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `so-giao-dich-${fromDate || ''}.csv`;
    a.click();
  };

  return (
    <section className="adm-stack">
      <AdminSectionTitle
        title={`Sổ giao dịch chi tiết ${data ? `(${data.totalCount.toLocaleString('vi-VN')} khoản)` : ''}`.trim()}
        extra={
          <AdminButton variant="soft" icon={<Download size={18} />} onClick={exportCsv}>
            Xuất CSV
          </AdminButton>
        }
      />
      <AdminCard flush>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '16px 20px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="adm-select"
              aria-label="Loại giao dịch"
              style={{ width: 200 }}
            >
              <option value="">Tất cả loại</option>
              {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="User ID (chi hoặc nhận)" className="adm-input" style={{ width: 220 }} />
            <input value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="Giá trị từ" inputMode="numeric" className="adm-input" style={{ width: 120 }} />
            <input value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="đến" inputMode="numeric" className="adm-input" style={{ width: 120 }} />
            <AdminButton variant="outline" onClick={load}>Lọc</AdminButton>
          </div>
          {data && (
            <div style={{ fontSize: 14, color: 'var(--adm-muted)' }}>
              Tổng chi khớp lọc: <b style={{ color: 'var(--adm-heading)' }}>{fmt(data.totalSpend)}</b>
              {' '}&nbsp; Tổng phí: <b style={{ color: 'var(--adm-heading)' }}>{fmt(data.totalFee)}</b>
            </div>
          )}
        </div>

        {loading ? (
          <div className="min-h-[120px] flex justify-center items-center"><Spin /></div>
        ) : !data || data.items.length === 0 ? (
          <p className="adm-muted" style={{ margin: 0, padding: '0 20px 20px', fontSize: 14 }}>Không có giao dịch khớp bộ lọc.</p>
        ) : (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Thời gian</th>
                    <th>Loại</th>
                    <th>Từ</th>
                    <th>Đến</th>
                    <th className="adm-num">Chi</th>
                    <th className="adm-num">Nhận</th>
                    <th className="adm-num">Phí</th>
                    <th>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((e: LedgerEntry) => (
                    <tr key={e.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(e.createdAt).toLocaleString('vi-VN')}</td>
                      <td>{TYPES[e.type] || e.type}</td>
                      <td className="font-mono">{short(e.fromUserId)}</td>
                      <td className="font-mono">{short(e.toUserId)}</td>
                      <td className="adm-num">{fmt(e.spendAmount)}</td>
                      <td className="adm-num">{fmt(e.receiveAmount)}</td>
                      <td className="adm-num">{fmt(e.feeAmount)}</td>
                      <td className="max-w-[240px] truncate" title={e.message || ''}>{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalCount > data.items.length && (
              <div style={{ padding: '16px 20px' }}>
                <AdminButton variant="link" onClick={() => setTake((t) => t + 100)}>
                  Tải thêm ({data.items.length}/{data.totalCount})
                </AdminButton>
              </div>
            )}
          </>
        )}
      </AdminCard>
    </section>
  );
};

export default TransactionLedger;
